import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MydataPageShell } from './MydataPageShell';
import { Callout, LoadingSpinner } from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import type {
  MydataPeriodSummary,
  MydataYearSummary,
} from '../../types/mydata';
import { MydataDebtsForm } from './MydataDebtsForm';
import { MydataDebtsYearTable } from './MydataDebtsYearTable';
import { MydataYearFilterBar } from './MydataYearFilterBar';

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function monthRange(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${year}-${pad2(month)}-01`,
    to: `${year}-${pad2(month)}-${pad2(lastDay)}`,
  };
}

function quarterRange(
  year: number,
  quarter: number,
): { from: string; to: string } {
  const startMonth = (quarter - 1) * 3 + 1;
  const endMonth = startMonth + 2;
  const lastDay = new Date(year, endMonth, 0).getDate();
  return {
    from: `${year}-${pad2(startMonth)}-01`,
    to: `${year}-${pad2(endMonth)}-${pad2(lastDay)}`,
  };
}

export function MydataDebtsPage() {
  const labels = t().mydata;
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [mode, setMode] = useState<'month' | 'quarter'>('month');
  const initial = monthRange(now.getFullYear(), now.getMonth() + 1);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [summary, setSummary] = useState<MydataPeriodSummary | null>(null);
  const [yearSummary, setYearSummary] = useState<MydataYearSummary | null>(
    null,
  );
  const [configured, setConfigured] = useState(false);
  const [loadingPeriod, setLoadingPeriod] = useState(true);
  const [loadingYear, setLoadingYear] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applyMonth = useCallback((y: number, m: number) => {
    const range = monthRange(y, m);
    setYear(y);
    setMonth(m);
    setDateFrom(range.from);
    setDateTo(range.to);
    setMode('month');
  }, []);

  const applyQuarter = useCallback((y: number, q: number) => {
    const range = quarterRange(y, q);
    setYear(y);
    setDateFrom(range.from);
    setDateTo(range.to);
    setMode('quarter');
  }, []);

  const changeYear = useCallback(
    (y: number) => {
      if (mode === 'quarter') {
        const q = Math.ceil(month / 3);
        applyQuarter(y, q);
      } else {
        applyMonth(y, month);
      }
    },
    [applyMonth, applyQuarter, mode, month],
  );

  useEffect(() => {
    let cancelled = false;
    async function loadConnection() {
      try {
        const connection = await api.getMydataConnection();
        if (!cancelled) setConfigured(Boolean(connection.configured));
      } catch {
        if (!cancelled) setConfigured(false);
      }
    }
    void loadConnection();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadYear() {
      setLoadingYear(true);
      try {
        const ys = await api.getMydataYearSummary({ year });
        if (!cancelled) setYearSummary(ys);
      } catch (err) {
        if (!cancelled) {
          setYearSummary(null);
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoadingYear(false);
      }
    }
    void loadYear();
    return () => {
      cancelled = true;
    };
  }, [year]);

  useEffect(() => {
    let cancelled = false;
    async function loadPeriod() {
      setLoadingPeriod(true);
      setError(null);
      try {
        const sum = await api.getMydataSummary({ dateFrom, dateTo });
        if (!cancelled) setSummary(sum);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
          setSummary(null);
        }
      } finally {
        if (!cancelled) setLoadingPeriod(false);
      }
    }
    void loadPeriod();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  const selectedMonth = mode === 'month' ? month : null;
  const selectedQuarter = useMemo(() => {
    if (mode !== 'quarter') return null;
    for (let q = 1; q <= 4; q += 1) {
      const range = quarterRange(year, q);
      if (range.from === dateFrom && range.to === dateTo) return q;
    }
    return null;
  }, [mode, year, dateFrom, dateTo]);

  return (
    <MydataPageShell>
      <div className="shrink-0 space-y-1">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {labels.debtsHint}
        </p>
        <div className="text-xs text-slate-500">
          <Link to="/mydata" className="text-brand hover:underline">
            {labels.periodTitle}
          </Link>
          {' · '}
          <Link to="/mydata/sync" className="text-brand hover:underline">
            {labels.syncPageLink}
          </Link>
          {' · '}
          <Link
            to="/settings/mydata-connection"
            className="text-brand hover:underline"
          >
            {labels.connectionTitle}
          </Link>
        </div>
      </div>

      {!configured ? (
        <Callout variant="warning" className="shrink-0">
          {labels.notConfigured}{' '}
          <Link to="/settings/mydata-connection" className="underline">
            {labels.connectionTitle}
          </Link>
        </Callout>
      ) : null}

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <MydataYearFilterBar year={year} onYearChange={changeYear} />

      {loadingYear ? (
        <LoadingSpinner />
      ) : (
        <MydataDebtsYearTable
          yearSummary={yearSummary}
          selectedMonth={selectedMonth}
          selectedQuarter={selectedQuarter}
          onSelectMonth={(m) => applyMonth(year, m)}
          onSelectQuarter={(q) => applyQuarter(year, q)}
        />
      )}

      <div className="shrink-0 border-t border-line pt-4">
        <h2 className="mb-3 text-sm font-semibold text-ink">
          {labels.debtsPeriodDetail}
        </h2>
        {loadingPeriod ? (
          <LoadingSpinner />
        ) : (
          <>
            {!summary?.synced ? (
              <Callout variant="info" className="shrink-0">
                {labels.noSummary}{' '}
                <Link to="/mydata/sync" className="underline">
                  {labels.sync}
                </Link>
              </Callout>
            ) : null}
            <MydataDebtsForm summary={summary} />
          </>
        )}
      </div>
    </MydataPageShell>
  );
}
